import axios, { type AxiosInstance, type AxiosResponse } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  Role,
  Menu,
  RoleMenu,
  UserRole,
  CreateRoleRequest,
  CreateMenuRequest,
  CreateRoleMenuRequest,
  CreateUserRoleRequest,
  DropdownOption,
  PaginatedResponse,
  ListParams,
  RoleMenuListParams,
  UserRoleListParams,
  RoleStats,
  ApiError,
} from "../Component/Application/rolemanagement/types"

// Enhanced API configuration
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
})

interface MenuListResponse {
  count: number
  results: Menu[]
}

// Request interceptor with better error handling
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}

    return config
  },
  (error) => {
    console.error("Request interceptor error:", error)
    return Promise.reject(error)
  },
)

// Response interceptor with comprehensive error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response time for performance monitoring
    // const endTime = new Date()
    // const duration = endTime.getTime() - response.config.?.startTime?.getTime()
    // console.debug(`API call to ${response.config.url} took ${duration}ms`)

    return response
  },
  (error) => {
    // Handle different error scenarios
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/login"
      return Promise.reject(new Error("Authentication required"))
    }

    if (error.response?.status === 403) {
      return Promise.reject(new Error("Access denied"))
    }

    if (error.response?.status >= 500) {
      return Promise.reject(new Error("Server error. Please try again later."))
    }

    // Extract meaningful error message
    const errorMessage =
      error.response?.data?.message || error.response?.data?.error || error.message || "An unexpected error occurred"

    console.error("API Error:", {
      url: error.config?.url,
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
    })

    return Promise.reject(new Error(errorMessage))
  },
)

// Enhanced Role Management Services
export const RoleService = {
  getAll: async (params?: ListParams): Promise<PaginatedResponse<Role>> => {
    try {
      const response = await api.get(`/api/core/roles/`, { params })
      return response.data
    } catch (error) {
      console.error("RoleService.getAll:", error)
      throw error
    }
  },

  getById: async (id: number): Promise<Role> => {
    try {
      const response = await api.get(`/api/core/roles/${id}/`)
      return response.data
    } catch (error) {
      console.error("RoleService.getById:", error)
      throw error
    }
  },

  create: async (data: CreateRoleRequest): Promise<Role> => {
    try {
      const response = await api.post(`/api/core/roles/create/`, data)
      return response.data
    } catch (error) {
      console.error("RoleService.create:", error)
      throw error
    }
  },

  update: async (id: number, data: Partial<CreateRoleRequest>): Promise<Role> => {
    try {
      const response = await api.put(`/api/core/roles/${id}/update/`, data)
      return response.data
    } catch (error) {
      console.error("RoleService.update:", error)
      throw error
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/core/roles/${id}/delete/`)
    } catch (error) {
      console.error("RoleService.delete:", error)
      throw error
    }
  },

  // Fixed stats endpoint - use a simpler approach or mock data
  getStats: async (id: number): Promise<RoleStats> => {
    try {
      // Try the stats endpoint first, fallback to calculating from other endpoints
      try {
        const response = await api.get(`/api/core/roles/${id}/stats/`)
        return response.data
      } catch (statsError) {
        console.warn("Stats endpoint not available, calculating manually")

        // Fallback: calculate stats from available data
        const [roleMenus, userRoles] = await Promise.all([
          api.get(`/api/core/role-menus/?role_id=${id}`),
          api.get(`/api/core/user-roles/?role_id=${id}`),
        ])

        const menuCount = roleMenus.data.count || 0
        const userCount = userRoles.data.count || 0
        const activeUsers = userRoles.data.results?.filter((ur: any) => ur.is_active).length || 0

        return {
          menu_count: menuCount,
          user_count: userCount,
          active_users: activeUsers,
          recent_assignments: 0, // Can't calculate without additional data
        }
      }
    } catch (error) {
      console.error("RoleService.getStats:", error)
      // Return default stats if everything fails
      return {
        menu_count: 0,
        user_count: 0,
        active_users: 0,
        recent_assignments: 0,
      }
    }
  },

  toggleStatus: async (id: number): Promise<Role> => {
    try {
      const response = await api.patch(`/api/core/roles/${id}/toggle-status/`)
      return response.data
    } catch (error) {
      console.error("RoleService.toggleStatus:", error)
      throw error
    }
  },
}

// Enhanced Menu Management Services
export const MenuService = {
  getAll: async (params?: ListParams): Promise<PaginatedResponse<Menu>> => {
    try {
      const response = await api.get(`/api/core/menus/`, { params })
      return response.data
    } catch (error) {
      console.error("MenuService.getAll:", error)
      throw error
    }
  },

  getAllUnpaginated: async (): Promise<MenuListResponse> => {
    try {
      const response = await api.get(`/api/core/menus/all/`)
      return response.data
    } catch (error) {
      console.error("MenuService.getAllUnpaginated:", error)
      throw error
    }
  },

  getTree: async (): Promise<Menu[]> => {
    try {
      const response = await api.get(`/api/core/menus/hierarchy/`)
      return response.data
    } catch (error) {
      console.error("MenuService.getTree:", error)
      throw error
    }
  },

  getById: async (id: number): Promise<Menu> => {
    try {
      const response = await api.get(`/api/core/menus/${id}/`)
      return response.data
    } catch (error) {
      console.error("MenuService.getById:", error)
      throw error
    }
  },

  create: async (data: CreateMenuRequest): Promise<Menu> => {
    try {
      const response = await api.post(`/api/core/menus/create/`, data)
      return response.data
    } catch (error) {
      console.error("MenuService.create:", error)
      throw error
    }
  },

  update: async (id: number, data: Partial<CreateMenuRequest>): Promise<Menu> => {
    try {
      const response = await api.put(`/api/core/menus/update/${id}/`, data)
      return response.data
    } catch (error) {
      console.error("MenuService.update:", error)
      throw error
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/core/menus/delete/${id}/`)
    } catch (error) {
      console.error("MenuService.delete:", error)
      throw error
    }
  },

  reorder: async (menuIds: number[]): Promise<void> => {
    try {
      await api.post(`/api/core/menus/reorder/`, { menu_ids: menuIds })
    } catch (error) {
      console.error("MenuService.reorder:", error)
      throw error
    }
  },
}

// Fixed Role Menu Management Services
export const RoleMenuService = {
  getAll: async (params?: RoleMenuListParams): Promise<PaginatedResponse<RoleMenu>> => {
    try {
      const response = await api.get(`/api/core/role-menus/`, { params })
      return response.data
    } catch (error) {
      console.error("RoleMenuService.getAll:", error)
      throw error
    }
  },

  // Fixed endpoint for getting role menus
  getByRole: async (roleId: number, params?: ListParams): Promise<PaginatedResponse<RoleMenu>> => {
    try {
      const queryParams = {
        role_id: roleId,
        ...params,
      }
      const response = await api.get(`/api/core/role-menus/`, { params: queryParams })
      return response.data
    } catch (error) {
      console.error("RoleMenuService.getByRole:", error)
      throw error
    }
  },

  create: async (data: CreateRoleMenuRequest): Promise<RoleMenu> => {
    try {
      const response = await api.post(`/api/core/role-menus/create/`, data)
      return response.data
    } catch (error) {
      console.error("RoleMenuService.create:", error)
      throw error
    }
  },

  bulkCreate: async (roleId: number, menuIds: number[]): Promise<void> => {
    try {
      const promises = menuIds.map((menuId) =>
        api.post(`/api/core/role-menus/`, {
          role: roleId,
          menu: menuId,
        }),
      )
      await Promise.all(promises)
    } catch (error) {
      console.error("RoleMenuService.bulkCreate:", error)
      throw error
    }
  },

  bulkUpdate: async (roleId: number, menuIds: number[]): Promise<void> => {
    try {
      // Delete existing assignments
      await api.delete(`/api/core/role-menus/bulk-delete/?role_id=${roleId}`)

      // Create new assignments
      const promises = menuIds.map((menuId) =>
        api.post(`/api/core/role-menus/`, {
          role: roleId,
          menu: menuId,
        }),
      )
      await Promise.all(promises)
    } catch (error) {
      console.error("RoleMenuService.bulkUpdate:", error)
      throw error
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/core/role-menus/${id}/delete/`)
    } catch (error) {
      console.error("RoleMenuService.delete:", error)
      throw error
    }
  },
}

// Enhanced User Role Management Services
export const UserRoleService = {
  getAll: async (params?: UserRoleListParams): Promise<PaginatedResponse<UserRole>> => {
    try {
      const response = await api.get(`/api/core/user-roles/`, { params })
      return response.data
    } catch (error) {
      console.error("UserRoleService.getAll:", error)
      throw error
    }
  },

  create: async (data: CreateUserRoleRequest): Promise<UserRole> => {
    try {
      const response = await api.post(`/api/core/user-roles/`, data)
      return response.data
    } catch (error) {
      console.error("UserRoleService.create:", error)
      throw error
    }
  },

  update: async (id: number, data: Partial<CreateUserRoleRequest>): Promise<UserRole> => {
    try {
      const response = await api.put(`/api/core/user-roles/${id}/`, data)
      return response.data
    } catch (error) {
      console.error("UserRoleService.update:", error)
      throw error
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/core/user-roles/${id}/`)
    } catch (error) {
      console.error("UserRoleService.delete:", error)
      throw error
    }
  },

  bulkAssign: async (userIds: number[], roleId: number): Promise<void> => {
    try {
      const promises = userIds.map((userId) =>
        api.post(`/api/core/user-roles/`, {
          user: userId,
          role: roleId,
        }),
      )
      await Promise.all(promises)
    } catch (error) {
      console.error("UserRoleService.bulkAssign:", error)
      throw error
    }
  },
}

// Enhanced Dropdown/Utility Services
export const DropdownService = {
  getRoles: async (): Promise<DropdownOption[]> => {
    try {
      const response = await api.get(`/api/core/roles/?limit=1000`)
      return response.data.results.map((role: Role) => ({
        id: role.id,
        name: role.name,
        title: role.name,
      }))
    } catch (error) {
      console.error("DropdownService.getRoles:", error)
      throw error
    }
  },

  getUsers: async (): Promise<DropdownOption[]> => {
    try {
      const response = await api.get(`/api/accounts/users/?limit=1000`)
      return response.data.results.map((user: any) => ({
        id: user.id,
        email: user.email,
        full_name: user.person?.full_name || user.email,
      }))
    } catch (error) {
      console.error("DropdownService.getUsers:", error)
      throw error
    }
  },

   getMenus: async (): Promise<DropdownOption[]> => {
    try {
      const response = await api.get(`/api/core/menus/all/`)
      return response.data.results.map((menu: Menu) => ({
        id: menu.id,
        name: menu.name,
        title: menu.title,
        type: menu.menu_type,
        parent_id: menu.parent,
      }))
    } catch (error) {
      console.error("DropdownService.getMenus:", error)
      throw error
    }
  },
}

// Enhanced Dynamic Menu Service
export const DynamicMenuService = {
  getUserMenus: async (): Promise<any[]> => {
    try {
      const response = await api.get(`/api/core/user/menus/`)
      return response.data
    } catch (error) {
      console.error("DynamicMenuService.getUserMenus:", error)
      throw error
    }
  },

  getMenusByRole: async (roleId?: number): Promise<any[]> => {
    try {
      const url = roleId ? `/api/core/menus/role/${roleId}/` : `/api/core/menus/role/`
      const response = await api.get(url)
      return response.data
    } catch (error) {
      console.error("DynamicMenuService.getMenusByRole:", error)
      throw error
    }
  },
}

// Utility functions
export const ApiUtils = {
  handleError: (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.message) {
      return error.message
    }
    return "An unexpected error occurred"
  },

  isNetworkError: (error: any): boolean => {
    return !error.response && error.request
  },

  getErrorDetails: (error: any): ApiError => {
    return {
      message: ApiUtils.handleError(error),
      errors: error.response?.data?.errors,
      status: error.response?.status,
    }
  },
}
